import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Quick help options for Invoice List tabs
 * Provides guided tours for common actions
 */
export const useInvoiceListQuickHelp = (
    tabType:
        | 'index'
        | 'waiting-confirm'
        | 'invoicable'
        | 'non-invoicable'
        | 'deposit'
) => {
    const options: QuickHelpOption[] = useMemo(() => {
        // Tab-specific options
        const getTabSpecificOptions = (): QuickHelpOption[] => {
            switch (tabType) {
                case 'index':
                    return [
                        {
                            id: 'invoice-view-stats',
                            label: 'Xem thống kê',
                            description: 'Xem tổng quan hoá đơn',
                            selector: '[data-tour="invoice-statistical"]',
                            tourTitle: 'Thống kê hoá đơn',
                            tourDescription:
                                'Xem tổng quan hoá đơn:\n\n• Tổng số hoá đơn theo trạng thái\n• Tổng doanh thu\n• Thống kê theo kỳ\n\nClick vào từng mục để lọc nhanh.',
                        },
                    ]

                case 'waiting-confirm':
                    return [
                        {
                            id: 'invoice-approve-invoicable',
                            label: 'Duyệt xuất HĐ',
                            description: 'Duyệt và xuất hoá đơn điện tử',
                            selector: '[data-tour="invoice-data-table"]',
                            tourTitle: 'Duyệt hoá đơn (Xuất HĐ)',
                            tourDescription:
                                'Duyệt hoá đơn và xuất HĐ điện tử:\n\n1. Tick chọn hoá đơn cần duyệt\n2. Click nút "Xuất HĐ"\n3. Xác nhận trong popup\n\n**Lưu ý:**\n• Hoá đơn sẽ chuyển sang tab "Cần xuất HĐ"\n• Cần nhập mã xuất HĐ sau khi duyệt',
                        },
                        {
                            id: 'invoice-approve-non-invoicable',
                            label: 'Duyệt không xuất HĐ',
                            description: 'Duyệt nhưng không xuất HĐ điện tử',
                            selector: '[data-tour="invoice-data-table"]',
                            tourTitle: 'Duyệt hoá đơn (Không xuất HĐ)',
                            tourDescription:
                                'Duyệt hoá đơn nhưng không xuất HĐ điện tử:\n\n1. Tick chọn hoá đơn cần duyệt\n2. Click nút "Không xuất HĐ"\n3. Xác nhận trong popup\n\n**Lưu ý:**\n• Hoá đơn sẽ chuyển sang tab "Không xuất HĐ"\n• Doanh thu vẫn được xác nhận',
                        },
                    ]

                case 'invoicable':
                    return [
                        {
                            id: 'invoice-enter-exporter-code',
                            label: 'Nhập số HĐ từ MISA',
                            description: 'Nhập số hoá đơn đã xuất từ MISA',
                            selector: '[data-tour="invoice-data-table"]',
                            tourTitle: 'Nhập số hoá đơn MISA',
                            tourDescription:
                                'Sau khi xuất hoá đơn từ hệ thống MISA:\n\n1. Hover vào cột "Mã xuất HĐ"\n2. Click vào icon bút chì (✏️)\n3. Nhập số HĐ từ MISA\n4. Nhấn Enter để lưu\n\n**Mẹo:**\n• Nhấn Escape để huỷ\n• Có thể copy/paste số HĐ từ MISA\n• Số HĐ được lưu tự động khi click ra ngoài',
                        },
                        {
                            id: 'invoice-misa-workflow',
                            label: 'Quy trình xuất HĐ',
                            description: 'Hướng dẫn quy trình xuất HĐĐT',
                            tourTitle: 'Quy trình xuất hoá đơn điện tử',
                            tourDescription:
                                'Quy trình xuất HĐĐT từ MISA:\n\n**Bước 1:** Xuất hoá đơn trên MISA\n• Đăng nhập MISA\n• Tạo hoá đơn điện tử\n• Lấy số hoá đơn đã xuất\n\n**Bước 2:** Cập nhật trên Datacentral\n• Tìm hoá đơn trong danh sách\n• Nhập số HĐ từ MISA vào cột "Mã xuất HĐ"\n• Lưu lại\n\n**Lưu ý:**\n• Số HĐ phải khớp với MISA\n• Mỗi hoá đơn chỉ có 1 số HĐ',
                        },
                    ]

                case 'non-invoicable':
                    return [
                        {
                            id: 'invoice-non-invoicable-info',
                            label: 'Thông tin tab',
                            description: 'Hiểu về hoá đơn không xuất',
                            tourTitle: 'Hoá đơn không xuất HĐ',
                            tourDescription:
                                'Tab này chứa hoá đơn đã duyệt nhưng không xuất HĐ điện tử:\n\n• Doanh thu đã được xác nhận\n• Không cần xuất hoá đơn điện tử\n• Thường là hoá đơn nội bộ hoặc đặc biệt\n\n**Lưu ý:**\n• Không thể thay đổi trạng thái\n• Liên hệ admin nếu cần điều chỉnh',
                        },
                    ]

                case 'deposit':
                    return [
                        {
                            id: 'invoice-deposit-info',
                            label: 'Thông tin tab',
                            description: 'Hiểu về hoá đơn nạp tiền',
                            tourTitle: 'Hoá đơn nạp tiền',
                            tourDescription:
                                'Tab này chứa hoá đơn từ giao dịch nạp tiền:\n\n• Hoá đơn tạo từ credit deposit\n• Theo dõi trạng thái thanh toán\n• Không hiển thị cột dịch vụ\n\n**Lưu ý:**\n• Hoá đơn tự động tạo khi nạp tiền\n• Liên hệ admin nếu cần điều chỉnh',
                        },
                    ]

                default:
                    return []
            }
        }

        // Common options for all tabs
        const commonOptions: QuickHelpOption[] = [
            {
                id: 'invoice-search',
                label: 'Tìm kiếm hoá đơn',
                description: 'Tìm nhanh theo nhiều tiêu chí',
                selector: '[data-tour="invoice-search"]',
                tourTitle: 'Tìm kiếm hoá đơn',
                tourDescription:
                    'Tìm kiếm nhanh hoá đơn:\n\n• Mã hoá đơn\n• Tên khách hàng\n• Mã dịch vụ\n\nKết quả hiển thị ngay khi gõ.',
            },
            {
                id: 'invoice-filter',
                label: 'Lọc danh sách',
                description: 'Sử dụng bộ lọc nâng cao',
                selector: '[data-tour="invoice-filter-bar"]',
                tourTitle: 'Bộ lọc hoá đơn',
                tourDescription:
                    'Lọc hoá đơn theo:\n\n• Trạng thái hoá đơn\n• Nhóm sản phẩm\n• Account Manager (CS phụ trách)\n• Ngày áp dụng\n\nKết hợp nhiều bộ lọc để tìm chính xác.',
            },
            {
                id: 'invoice-no-account-manager',
                label: 'Lọc chưa phân công CS',
                description: 'Tìm hoá đơn chưa có CS phụ trách',
                selector: '[data-tour="invoice-filter-bar"]',
                tourTitle: 'Lọc hoá đơn chưa phân công CS',
                tourDescription:
                    'Tìm hoá đơn chưa có Account Manager:\n\n1. Click vào bộ lọc "Trạng thái CS"\n2. Chọn "Chưa phân công"\n3. Hoá đơn sẽ được lọc tự động\n\n**Mẹo:**\n• Kết hợp với các bộ lọc khác\n• Hữu ích để phân công CS mới',
            },
            {
                id: 'invoice-table-settings',
                label: 'Tuỳ chỉnh bảng',
                description: 'Cài đặt hiển thị bảng',
                selector: '[data-tour="invoice-settings"]',
                tourTitle: 'Cài đặt bảng',
                tourDescription:
                    'Click icon Settings để:\n\n• Điều chỉnh cỡ chữ\n• Thay đổi chiều cao hàng\n• Ẩn/hiện cột\n• Reset về mặc định\n\nCài đặt được lưu tự động.',
            },
            {
                id: 'invoice-overview-tour',
                label: 'Xem tour tổng quan',
                description: 'Hướng dẫn sử dụng trang',
                tourTitle: 'Tour hướng dẫn',
                tourDescription:
                    'Nhấn nút "Hướng dẫn sử dụng" ở góc phải màn hình để xem tour chi tiết các tính năng quản lý hoá đơn.',
            },
        ]

        return [...getTabSpecificOptions(), ...commonOptions]
    }, [tabType])

    useQuickHelp(options)

    return { options }
}

export default useInvoiceListQuickHelp
